import {
  absBig,
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  gcdBig,
  isqrtBig,
  modPowBig,
  pow10Big,
} from "@/lib/tool-kit/math/bigint";

/**
 * Prime factorisation for integers up to 10^20.
 *
 * Three tiers, chosen by size:
 *
 * 1. Up to 10^12 the number fits in a double exactly and its square root is at most a
 *    million, so trial division on a 6k ± 1 wheel (2, 3, then 5, 7, 11, 13, …) finishes
 *    in a few milliseconds with plain Numbers.
 * 2. Above that, small factors are stripped by the same wheel on BigInt, then whatever
 *    remains is tested with Miller–Rabin. The twelve prime bases 2…37 make the test
 *    deterministic below about 3.3 × 10^24, far above this module's ceiling.
 * 3. A composite remainder is split with Pollard's rho in Brent's form. Its starting
 *    point and constant are fixed, not random, so the server render and the browser
 *    render agree and the same input always takes the same path.
 */

export interface PrimePower {
  prime: bigint;
  exponent: number;
}

export interface Factorization {
  factors: PrimePower[];
  /** False only if Pollard's rho gave up on a composite — never seen below 10^20. */
  complete: boolean;
}

/**
 * The largest number the factoriser accepts. The hardest case below it — a product of
 * two ten-digit primes — takes Pollard–Brent about 25 ms, fast enough to run as you
 * type.
 */
export const MAX_FACTOR_INPUT = pow10Big(20);

const NUMBER_TRIAL_LIMIT = 1e12;
const BIG_TRIAL_LIMIT = 65_536;
const MR_BASES = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37].map((p) =>
  BigInt(p)
);
const RHO_BLOCK = 128;
const RHO_MAX_CYCLE = 1_048_576;
const RHO_ATTEMPTS = 24;

/* ------------------------------------------------------------ primality */

function millerRabinRound(n: bigint, base: bigint, d: bigint, s: number) {
  let x = modPowBig(base, d, n);
  const minusOne = n - BIG_ONE;
  if (x === BIG_ONE || x === minusOne) {
    return true;
  }
  for (let round = 1; round < s; round += 1) {
    x = (x * x) % n;
    if (x === minusOne) {
      return true;
    }
  }
  return false;
}

/** Deterministic primality test for every n below about 3.3 × 10^24. */
export function isPrimeBig(n: bigint): boolean {
  if (n < BIG_TWO) {
    return false;
  }
  for (const p of MR_BASES) {
    if (n === p) {
      return true;
    }
    if (n % p === BIG_ZERO) {
      return false;
    }
  }
  let d = n - BIG_ONE;
  let s = 0;
  while (d % BIG_TWO === BIG_ZERO) {
    d /= BIG_TWO;
    s += 1;
  }
  return MR_BASES.every((base) => millerRabinRound(n, base, d, s));
}

/* ------------------------------------------------------- trial division */

type Tally = Map<bigint, number>;

function tally(out: Tally, prime: bigint, times = 1): void {
  out.set(prime, (out.get(prime) ?? 0) + times);
}

/** Divide out `p` as often as it goes; returns what is left. */
function stripNumber(m: number, p: number, out: Tally): number {
  let rest = m;
  while (rest % p === 0) {
    tally(out, BigInt(p));
    rest /= p;
  }
  return rest;
}

/** Full trial division of a safe integer on the 6k ± 1 wheel. */
function trialFactorNumber(n: number, out: Tally): void {
  let rest = stripNumber(stripNumber(n, 2, out), 3, out);
  for (let f = 5; f * f <= rest; f += 6) {
    rest = stripNumber(rest, f, out);
    rest = stripNumber(rest, f + 2, out);
  }
  if (rest > 1) {
    tally(out, BigInt(rest));
  }
}

function stripBig(m: bigint, p: bigint, out: Tally): bigint {
  let rest = m;
  while (rest % p === BIG_ZERO) {
    tally(out, p);
    rest /= p;
  }
  return rest;
}

/** Strip every prime factor below 2^16 from a BigInt. */
function stripSmallBig(n: bigint, out: Tally): bigint {
  let rest = stripBig(stripBig(n, BIG_TWO, out), BigInt(3), out);
  for (let f = 5; f <= BIG_TRIAL_LIMIT; f += 6) {
    const big = BigInt(f);
    if (big * big > rest) {
      break;
    }
    rest = stripBig(rest, big, out);
    rest = stripBig(rest, BigInt(f + 2), out);
  }
  return rest;
}

/* --------------------------------------------------------- Pollard–Brent */

function brentBlock(
  n: bigint,
  step: (v: bigint) => bigint,
  start: { x: bigint; y: bigint; q: bigint },
  length: number
) {
  let { y, q } = start;
  for (let i = 0; i < length; i += 1) {
    y = step(y);
    q = (q * absBig(start.x - y)) % n;
  }
  return { y, q };
}

/** One run of Brent's cycle-finding with a fixed polynomial x² + c. */
function brentAttempt(n: bigint, c: bigint): bigint | null {
  const step = (v: bigint) => (v * v + c) % n;
  let y = BIG_TWO;
  let x = y;
  let ys = y;
  let q = BIG_ONE;
  let g = BIG_ONE;
  let cycle = 1;
  while (g === BIG_ONE) {
    x = y;
    for (let i = 0; i < cycle; i += 1) {
      y = step(y);
    }
    let k = 0;
    while (k < cycle && g === BIG_ONE) {
      ys = y;
      const block = brentBlock(
        n,
        step,
        { x, y, q },
        Math.min(RHO_BLOCK, cycle - k)
      );
      y = block.y;
      q = block.q;
      g = gcdBig(q, n);
      k += RHO_BLOCK;
    }
    cycle *= 2;
    if (cycle > RHO_MAX_CYCLE) {
      return null;
    }
  }
  if (g === n) {
    g = BIG_ONE;
    while (g === BIG_ONE) {
      ys = step(ys);
      g = gcdBig(absBig(x - ys), n);
    }
  }
  return g === n ? null : g;
}

/** A nontrivial factor of the composite `n`, or null if every attempt failed. */
function pollardBrent(n: bigint): bigint | null {
  if (n % BIG_TWO === BIG_ZERO) {
    return BIG_TWO;
  }
  for (let c = 1; c <= RHO_ATTEMPTS; c += 1) {
    const factor = brentAttempt(n, BigInt(c));
    if (factor !== null) {
      return factor;
    }
  }
  return null;
}

/** Split `m` (no factor below 2^16) into primes; returns false if rho gave up. */
function splitLarge(m: bigint, out: Tally): boolean {
  if (m === BIG_ONE) {
    return true;
  }
  if (isPrimeBig(m)) {
    tally(out, m);
    return true;
  }
  // Rho is slow on a perfect square; split the root once and count it twice.
  const root = isqrtBig(m);
  if (root * root === m) {
    const half: Tally = new Map();
    const ok = splitLarge(root, half);
    for (const [prime, count] of half) {
      tally(out, prime, 2 * count);
    }
    return ok;
  }
  const factor = pollardBrent(m);
  if (factor === null) {
    tally(out, m);
    return false;
  }
  const first = splitLarge(factor, out);
  const second = splitLarge(m / factor, out);
  return first && second;
}

/**
 * The prime factorisation of n ≥ 2 (up to `MAX_FACTOR_INPUT`), primes ascending.
 * Returns no factors for n < 2.
 */
export function factorize(n: bigint): Factorization {
  const out: Tally = new Map();
  let complete = true;
  if (n < BIG_TWO) {
    return { factors: [], complete };
  }
  if (n <= BigInt(NUMBER_TRIAL_LIMIT)) {
    trialFactorNumber(Number(n), out);
  } else {
    const rest = stripSmallBig(n, out);
    if (rest !== BIG_ONE && rest <= BigInt(NUMBER_TRIAL_LIMIT)) {
      trialFactorNumber(Number(rest), out);
    } else {
      complete = splitLarge(rest, out);
    }
  }
  const factors = [...out.entries()]
    .map(([prime, exponent]) => ({ prime, exponent }))
    .sort((a, b) => (a.prime < b.prime ? -1 : 1));
  return { factors, complete };
}

/* ------------------------------------------------------------- divisors */

/** τ(n) = Π (eᵢ + 1). */
export function divisorCount(factors: readonly PrimePower[]): number {
  return factors.reduce((count, f) => count * (f.exponent + 1), 1);
}

/** Every divisor, ascending — or null when there are more than `limit`. */
export function listDivisors(
  factors: readonly PrimePower[],
  limit: number
): bigint[] | null {
  if (divisorCount(factors) > limit) {
    return null;
  }
  let divisors: bigint[] = [BIG_ONE];
  for (const { prime, exponent } of factors) {
    const next: bigint[] = [];
    for (const d of divisors) {
      let power = BIG_ONE;
      for (let e = 0; e <= exponent; e += 1) {
        next.push(d * power);
        power *= prime;
      }
    }
    divisors = next;
  }
  return divisors.sort((a, b) => (a < b ? -1 : 1));
}

/** The number the factors multiply back to. */
export function productOf(factors: readonly PrimePower[]): bigint {
  let product = BIG_ONE;
  for (const { prime, exponent } of factors) {
    product *= prime ** BigInt(exponent);
  }
  return product;
}

/** Ω(n): the prime factors counted with multiplicity. */
export function primeFactorTotal(factors: readonly PrimePower[]): number {
  return factors.reduce((sum, f) => sum + f.exponent, 0);
}

/* ---------------------------------------------------------- factor tree */

export interface FactorTreeNode {
  value: bigint;
  /** Empty for a prime leaf; otherwise [smaller factor, larger factor]. */
  children: FactorTreeNode[];
}

/** Every exponent vector of a divisor of the node, as products. */
function nodeDivisors(primes: readonly bigint[], exponents: readonly number[]) {
  let options: { value: bigint; exps: number[] }[] = [
    { value: BIG_ONE, exps: [] },
  ];
  for (const [index, prime] of primes.entries()) {
    const next: typeof options = [];
    for (const option of options) {
      let power = BIG_ONE;
      for (let e = 0; e <= exponents[index]; e += 1) {
        next.push({ value: option.value * power, exps: [...option.exps, e] });
        power *= prime;
      }
    }
    options = next;
  }
  return options;
}

function buildNode(
  primes: readonly bigint[],
  exponents: readonly number[]
): FactorTreeNode {
  let value = BIG_ONE;
  for (const [index, prime] of primes.entries()) {
    value *= prime ** BigInt(exponents[index]);
  }
  const total = exponents.reduce((sum, e) => sum + e, 0);
  if (total <= 1) {
    return { value, children: [] };
  }
  // The split a person would draw: the factor pair closest to √n.
  let best = { value: BIG_ONE, exps: exponents.map(() => 0) };
  for (const option of nodeDivisors(primes, exponents)) {
    if (option.value * option.value <= value && option.value > best.value) {
      best = option;
    }
  }
  const rest = exponents.map((e, index) => e - best.exps[index]);
  return {
    value,
    children: [buildNode(primes, best.exps), buildNode(primes, rest)],
  };
}

/** A balanced factor tree: each composite splits into its factor pair nearest √n. */
export function buildFactorTree(
  factors: readonly PrimePower[]
): FactorTreeNode {
  return buildNode(
    factors.map((f) => f.prime),
    factors.map((f) => f.exponent)
  );
}

export interface LadderStep {
  dividend: bigint;
  divisor: bigint;
  quotient: bigint;
}

/** Repeated division by the smallest prime factor — the "ladder" method. */
export function divisionLadder(
  n: bigint,
  factors: readonly PrimePower[]
): LadderStep[] {
  const steps: LadderStep[] = [];
  let current = n;
  for (const { prime, exponent } of factors) {
    for (let e = 0; e < exponent; e += 1) {
      const quotient = current / prime;
      steps.push({ dividend: current, divisor: prime, quotient });
      current = quotient;
    }
  }
  return steps;
}

/* ------------------------------------------------------------- notation */

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

/** 12 → "¹²", -3 → "⁻³". */
export function toSuperscript(n: number): string {
  const sign = n < 0 ? "⁻" : "";
  const digits = Math.abs(n)
    .toString()
    .split("")
    .map((d) => SUPERSCRIPT_DIGITS[Number(d)])
    .join("");
  return `${sign}${digits}`;
}

/** "2³ × 3² × 5" */
export function exponentForm(factors: readonly PrimePower[]): string {
  return factors
    .map(({ prime, exponent }) =>
      exponent === 1 ? prime.toString() : `${prime}${toSuperscript(exponent)}`
    )
    .join(" × ");
}

/** "2 × 2 × 2 × 3 × 3 × 5" */
export function expandedForm(factors: readonly PrimePower[]): string {
  const parts: string[] = [];
  for (const { prime, exponent } of factors) {
    for (let e = 0; e < exponent; e += 1) {
      parts.push(prime.toString());
    }
  }
  return parts.join(" × ");
}

/** "2^3 * 3^2 * 5" — the form a calculator or spreadsheet accepts. */
export function plainExponentForm(factors: readonly PrimePower[]): string {
  return factors
    .map(({ prime, exponent }) =>
      exponent === 1 ? prime.toString() : `${prime}^${exponent}`
    )
    .join(" * ");
}
